import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import { gloss, inkA } from "../theme";
import * as haptics from "../haptics";
import { MsBtn } from "../ms";
import type { MsBtnVariant } from "../ms";

const DEFAULT_HOLD_MS = 900;

/** The wipe has to read against the face it's drawn on: light ink flooding a
 *  paper/ghost button, white lifting a red/ink one. */
const wipeFor = (variant: MsBtnVariant) =>
    variant === "red" || variant === "ink" ? gloss(0.26) : inkA(0.14);

/* These are ours, not the DOM's. styled(Component) forwards every prop by
 * default, and plausible-looking names would land on the <button>. */
const faceConfig = {
    shouldForwardProp: (prop: string) => !["holding", "holdMs", "compact"].includes(prop),
};

/**
 * The press itself. An ink wipe crosses the face as the hold progresses, drawn
 * over the button's own background but under the label.
 *
 * The wipe is a single CSS transition rather than a per-frame JS loop: the
 * browser interpolates it off the main thread, and a hold costs two React
 * renders (down, up) instead of one per frame for its whole duration.
 *
 * It is deliberately *not* gated on reduced-motion — it isn't decoration, it's
 * the only thing telling you how much longer to hold. index.css's global rule
 * would collapse it, so it opts back out explicitly.
 */
const Face = styled(MsBtn, faceConfig)<{ holding: boolean; holdMs: number; compact?: boolean }>`
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: ${p => p.holding ? "100%" : "0%"};
        background: ${p => wipeFor(p.variant ?? "red")};
        pointer-events: none;
        /* Filling takes exactly the hold; releasing snaps back quickly so a
           cancel reads as a cancel. */
        transition: width ${p => p.holding ? `${p.holdMs}ms linear` : "160ms ease-out"};

        @media (prefers-reduced-motion: reduce) {
            transition-duration: ${p => p.holding ? `${p.holdMs}ms` : "0ms"} !important;
        }
    }

    ${p => p.compact && css`
        padding: 10px 16px;
        font-size: 13px;
    `}
`;

/** Sits above the wipe. */
const Label = styled.span`
    position: relative;
    z-index: 1;
`;

interface Props {
    onConfirm: () => void;
    /** Resting label. Should say "hold" — it's the only cue that this isn't an
     *  ordinary button. */
    text: string;
    /** Label swapped in while held. Falls back to `text`. */
    activeText?: string;
    variant?: MsBtnVariant;
    /** Tighter padding/type for secondary placements (e.g. under a CTA). */
    compact?: boolean;
    /** How long the user must hold, in ms. */
    holdMs?: number;
    disabled?: boolean;
    className?: string;
}

/**
 * Hold-to-confirm for destructive actions — end the game, clear your answer.
 *
 * Built on MsBtn so it reads as the same paper control as everything else,
 * rather than the flat white/red utility button it used to be. Crucially it now
 * has a keyboard path: hold Space or Enter. It previously bound only mouse and
 * touch, which left the actions it guards unreachable without a pointer.
 */
export default function HoldToConfirmButton({
    onConfirm,
    text,
    activeText,
    variant = "red",
    compact,
    holdMs = DEFAULT_HOLD_MS,
    disabled,
    className,
}: Props) {
    const [holding, setHolding] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Parked in a ref so the pending timer never closes over a stale callback
    // and doesn't need to restart when the parent re-renders.
    const onConfirmRef = useRef(onConfirm);
    useEffect(() => { onConfirmRef.current = onConfirm; }, [onConfirm]);

    const stop = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
            // A pending timer here means the hold was abandoned, not completed —
            // `tick` is the lightest mark in the set precisely so that letting go
            // early feels like nothing happening, because nothing did.
            haptics.tick();
        }
        setHolding(false);
    }, []);

    const start = useCallback(() => {
        if (disabled || timerRef.current !== null) return;
        haptics.tap();
        setHolding(true);
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            setHolding(false);
            // Not `submit`: this button exists to guard the irreversible ones.
            haptics.destructive();
            onConfirmRef.current();
        }, holdMs);
    }, [disabled, holdMs]);

    // A hold left pending across an unmount would fire into a dead tree.
    useEffect(() => () => {
        if (timerRef.current !== null) clearTimeout(timerRef.current);
    }, []);

    const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
        if (e.key !== " " && e.key !== "Enter") return;
        // Space scrolls and Enter synthesises a click; this button must only
        // ever fire from a completed hold.
        e.preventDefault();
        // Held keys auto-repeat — without this the hold restarts every repeat
        // and can never finish.
        if (e.repeat) return;
        start();
    };

    const handleKeyUp = (e: KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === " " || e.key === "Enter") stop();
    };

    return (
        <Face
            type="button"
            variant={variant}
            compact={compact}
            holding={holding}
            holdMs={holdMs}
            disabled={disabled}
            className={className}
            // This button runs the whole gesture itself — press, abandon,
            // confirm. A pointerup still synthesises a click, so without this
            // MsBtn's own variant haptic would fire on top of every one of them.
            haptic={null}
            onPointerDown={start}
            onPointerUp={stop}
            onPointerLeave={stop}
            onPointerCancel={stop}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            // Losing focus mid-hold (tab away, popup closes) must not leave a
            // loop running that fires the action later.
            onBlur={stop}
            onContextMenu={e => e.preventDefault()}
        >
            <Label>{holding && activeText ? activeText : text}</Label>
        </Face>
    );
}
